/* src/components/tutorial/TutorialScreen.js */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import GameHeader from '../game/GameHeader';
import PositionGrid from '../game/PositionGrid';
import AudioLetterDisplay from '../game/AudioLetterDisplay';
import MatchButton from '../game/MatchButton';
import { formatCombinedDifficulty } from '../../contexts/gameHelpers';
import audioManager from '../../utils/audioManager'; // Use the new audio manager
import { useSettings } from '../../contexts/SettingsContext'; // To get selected voice

// --- Define Tutorial Sequences HERE (KEEP THIS SET) ---
const SEQ_1BACK_VISUAL_FIXED = [ // Target matches: index 2, 4
    { position: 1, letter: null }, // 0
    { position: 5, letter: null }, // 1
    { position: 5, letter: null }, // 2 (Match T1)
    { position: 8, letter: null }, // 3
    { position: 8, letter: null }  // 4 (Match T3)
];
const SEQ_1BACK_AUDIO_FIXED = [ // Target matches: index 2, 4
    { position: null, letter: 'K' }, // 0
    { position: null, letter: 'T' }, // 1
    { position: null, letter: 'T' }, // 2 (Match T1)
    { position: null, letter: 'S' }, // 3
    { position: null, letter: 'S' }  // 4 (Match T3)
];
const SEQ_2BACK_VISUAL_FIXED = [ // Target matches: index 2, 5
    { position: 1, letter: null }, // 0
    { position: 5, letter: null }, // 1
    { position: 1, letter: null }, // 2 (Match T0)
    { position: 8, letter: null }, // 3
    { position: 7, letter: null }, // 4
    { position: 8, letter: null }, // 5 (Match T3)
    { position: 3, letter: null }  // 6
];
const SEQ_2BACK_AUDIO_FIXED = [ // Target matches: index 3, 6
    { position: null, letter: 'F' }, // 0
    { position: null, letter: 'P' }, // 1
    { position: null, letter: 'G' }, // 2
    { position: null, letter: 'F' }, // 3 (Match T0=F)
    { position: null, letter: 'D' }, // 4
    { position: null, letter: 'B' }, // 5
    { position: null, letter: 'D' }  // 6 (Match T4)
];
const SEQ_2BACK_DUAL_SIMPLIFIED = [ // Matches: V@2, A@3, D@4
    { position: 1, letter: 'K' }, // 0
    { position: 5, letter: 'T' }, // 1
    { position: 1, letter: 'S' }, // 2 (Visual Match w T0)
    { position: 8, letter: 'T' }, // 3 (Audio Match w T1)
    { position: 1, letter: 'S' }  // 4 (Dual Match w T2)
];

// --- Tutorial Stages and Steps Configuration ---
const TUTORIAL_CONFIG = {
    intro: [
        { text: "Welcome! Let's learn Dual N-Back step-by-step. This exercise trains your working memory." },
        { text: "You'll see flashing squares on a grid and hear spoken letters. Your goal is to spot when a square or letter repeats from 'N' steps back." }
    ],
    '1back_visual_intro': [
        { text: "First, let's try 1-Back with just the visual squares. You're looking for a square that appears in the SAME position as the one just before it (1 step back)." }
    ],
    '1back_visual_play': [
        { text: "Trial 1: A square appears. Remember its position.", trial: 0 },
        { text: "Trial 2: A new square. Is it in the same position as Trial 1? (No)", trial: 1 },
        { text: "Trial 3: MATCH! This position (5) is the same as Trial 2 (5). Press 'Position Match' (←).", trial: 2, promptButton: 'visual' },
        { text: "Trial 4: New square. Same position as Trial 3? (No)", trial: 3 },
        { text: "Trial 5: MATCH! This position (8) is the same as Trial 4 (8). Press 'Position Match' (←).", trial: 4, promptButton: 'visual' }
    ],
    '1back_audio_intro': [
        { text: "Great! Now let's try 1-Back with just the audio letters. Listen for a letter that's the SAME as the one just before it." }
    ],
    '1back_audio_play': [
        { text: "Trial 1: Listen to the letter.", trial: 0 },
        { text: "Trial 2: New letter. Same as Trial 1? (No)", trial: 1 },
        { text: "Trial 3: MATCH! This letter 'T' is the same as Trial 2 'T'. Press 'Audio Match' (→).", trial: 2, promptButton: 'audio' },
        { text: "Trial 4: New letter. Same as Trial 3? (No)", trial: 3 },
        { text: "Trial 5: MATCH! This letter 'S' is the same as Trial 4 'S'. Press 'Audio Match' (→).", trial: 4, promptButton: 'audio' }
    ],
    '2back_intro': [
        { text: "Okay, let's level up to 2-Back. Now you look for repeats from TWO steps back. Helpers will show positions '1' and '2' trials ago." }
    ],
    '2back_visual_play': [
        { text: "Trial 1: Remember this position (1).", trial: 0 },
        { text: "Trial 2: Remember this position (5).", trial: 1 },
        { text: "Trial 3: MATCH! Position (1) is same as T1 (helper '2'). Press 'Position Match' (←).", trial: 2, promptButton: 'visual' }, // Should be Match w T0 = 1
        { text: "Trial 4: New square (8). Compare to T2 (5) (helper '2'). Match? (No)", trial: 3 },
        { text: "Trial 5: New square (7). Compare to T3 (1) (helper '2'). Match? (No)", trial: 4 },
        { text: "Trial 6: MATCH! Position (8) is same as T4 (8) (helper '2'). Press 'Position Match' (←).", trial: 5, promptButton: 'visual' }, // Should be Match w T3 = 8
        { text: "Trial 7: New square (3). Compare to T5 (7) (helper '2'). Match? (No)", trial: 6 }
    ],
     '2back_audio_play': [
        { text: "Trial 1: Remember letter 'F'.", trial: 0 },
        { text: "Trial 2: Remember letter 'P'.", trial: 1 },
        { text: "Trial 3: New letter 'G'. Compare to T1 'F' (helper '2'). Match? (No)", trial: 2 }, // Should compare to T0 = 'F'
        { text: "Trial 4: MATCH! Letter 'F' is same as T0 'F' (helper '2'). Press 'Audio Match' (→).", trial: 3, promptButton: 'audio' },
        { text: "Trial 5: New letter 'D'. Compare to T3 'G' (helper '2'). Match? (No)", trial: 4 },
        { text: "Trial 6: New letter 'B'. Compare to T4 'D' (helper '2'). Match? (No)", trial: 5 },
        { text: "Trial 7: MATCH! Letter 'D' is same as T4 'D' (helper '2'). Press 'Audio Match' (→).", trial: 6, promptButton: 'audio' }
    ],
    'dual_intro': [
        { text: "Now for the full challenge: Dual 2-Back! Track BOTH squares and letters independently. Look for matches from 2 steps back for each." }
    ],
    'dual_play': [
        { text: "Trial 1: Track Position (1) and Letter 'K'.", trial: 0 },
        { text: "Trial 2: Track Position (5) and Letter 'T'.", trial: 1 },
        { text: "Trial 3: Position (1) matches T1 (helper '2'). Visual Match! Letter 'S' doesn't match T1 'K'. Press 'Position Match' (←).", trial: 2, promptButton: 'visual' }, // Visual matches T0=1
        { text: "Trial 4: Position (8) doesn't match T2 (5). Letter 'T' matches T2 'T' (helper '2'). Audio Match! Press 'Audio Match' (→).", trial: 3, promptButton: 'audio' }, // Audio matches T1=T
        { text: "Trial 5: Position (1) matches T3 (1). Letter 'S' matches T3 ('S'). Dual Match! Press BOTH buttons!", trial: 4, promptButton: 'both' } // Pos matches T2=1, Audio matches T2=S
    ],
    'outro': [
        { text: "Congratulations! You've completed the tutorial. Remember, N-Back is challenging, but consistent practice helps. You can revisit this anytime from the dashboard." }
    ]
};

// Map stage keys to sequences
const STAGE_SEQUENCE_MAP = {
    '1back_visual_play': { seq: SEQ_1BACK_VISUAL_FIXED, nBack: 1, modality: 'visual' },
    '1back_audio_play': { seq: SEQ_1BACK_AUDIO_FIXED, nBack: 1, modality: 'audio' },
    '2back_visual_play': { seq: SEQ_2BACK_VISUAL_FIXED, nBack: 2, modality: 'visual' },
    '2back_audio_play': { seq: SEQ_2BACK_AUDIO_FIXED, nBack: 2, modality: 'audio' },
    'dual_play': { seq: SEQ_2BACK_DUAL_SIMPLIFIED, nBack: 2, modality: 'dual' },
};


/**
 * TutorialScreen Component
 * Guides the user through the basics of Dual N-Back.
 */
const TutorialScreen = () => {
    // --- Hooks ---
    const navigate = useNavigate();
    const { settings } = useSettings();
    const defaultVoiceId = useMemo(() => Object.keys(audioManager.VOICES)[0] || null, []);
    const selectedVoiceId = settings?.selectedVoiceId || defaultVoiceId;

    const [stage, setStage] = useState('intro');
    const [step, setStep] = useState(0);
    // State for the simulated game display
    const [currentNBack, setCurrentNBack] = useState(1);
    const [currentModality, setCurrentModality] = useState('none');
    const [currentSequence, setCurrentSequence] = useState([]);
    const [currentTrialIndex, setCurrentTrialIndex] = useState(-1);
    const [totalTrials, setTotalTrials] = useState(0);
    // State for button interaction prompts and feedback
    const [promptButton, setPromptButton] = useState(null);
    const [feedbackState, setFeedbackState] = useState({ visual: null, audio: null });

    // --- Derived State ---
    const currentStepConfig = TUTORIAL_CONFIG[stage]?.[step] || null;
    const currentStageConfig = STAGE_SEQUENCE_MAP[stage];

    // --- Initialize Audio Manager ---
    useEffect(() => {
        // audioManager.init(); // Assume init elsewhere
        audioManager.resume(); // Attempt resume on mount
    }, []);

    // --- Effect to update game view and play audio letter ---
    useEffect(() => {
        let letterToSpeak = null;
        let voiceIdToUse = selectedVoiceId;

        audioManager.stopCurrentSound(); // Stop previous sound

        if (currentStepConfig) {
            if (currentStepConfig.trial !== undefined && currentStageConfig) {
                setCurrentNBack(currentStageConfig.nBack);
                setCurrentModality(currentStageConfig.modality);
                setCurrentSequence(currentStageConfig.seq);
                setTotalTrials(currentStageConfig.seq.length);
                setCurrentTrialIndex(currentStepConfig.trial);
                setPromptButton(currentStepConfig.promptButton || null);
                setFeedbackState({ visual: null, audio: null });

                const sequenceItem = currentStageConfig.seq[currentStepConfig.trial];
                letterToSpeak = sequenceItem?.letter;
                // voiceIdToUse = sequenceItem?.voice || selectedVoiceId; // Use if voice stored per item
            } else {
                setCurrentTrialIndex(-1);
                setCurrentModality('none');
                setPromptButton(null);
                setCurrentNBack(1);
                setCurrentSequence([]);
                setTotalTrials(0);
            }
        }

        if (letterToSpeak && (currentModality === 'audio' || currentModality === 'dual')) {
             audioManager.resume().then(resumed => {
                if (resumed) {
                    if (!voiceIdToUse) {
                        console.warn("[Tutorial] Cannot play audio: No voice selected or available.");
                        return;
                    }
                    const speakTimer = setTimeout(() => {
                        // console.log(`[Tutorial] Playing: ${letterToSpeak}, Voice: ${voiceIdToUse}`);
                        audioManager.playItem(
                            letterToSpeak,
                            0, // Difficulty 0 for tutorial
                            { voiceId: voiceIdToUse }
                        );
                    }, 150);
                    // TODO: Manage timer ref if cleanup needed on rapid step changes
                } else {
                     console.warn("[Tutorial] Audio context not resumed, skipping audio playback.");
                }
            });
        }
    }, [stage, step, currentStepConfig, currentStageConfig, currentModality, selectedVoiceId]);


    // --- Navigation Handler ---
    const handleNext = useCallback(() => {
        setFeedbackState({ visual: null, audio: null });
        audioManager.stopCurrentSound();

        const currentStepsInStage = TUTORIAL_CONFIG[stage]?.length || 0;
        let nextStage = stage;
        let nextStep = step + 1;

        if (nextStep >= currentStepsInStage) {
            switch (stage) {
                case 'intro':               nextStage = '1back_visual_intro'; break;
                case '1back_visual_intro':  nextStage = '1back_visual_play'; break;
                case '1back_visual_play':   nextStage = '1back_audio_intro'; break;
                case '1back_audio_intro':   nextStage = '1back_audio_play'; break;
                case '1back_audio_play':    nextStage = '2back_intro'; break;
                case '2back_intro':         nextStage = '2back_visual_play'; break;
                case '2back_visual_play':   nextStage = '2back_audio_play'; break;
                case '2back_audio_play':    nextStage = 'dual_intro'; break;
                case 'dual_intro':          nextStage = 'dual_play'; break;
                case 'dual_play':           nextStage = 'outro'; break;
                case 'outro':
                    navigate('/dashboard');
                    return;
                default:
                    console.warn(`Unhandled tutorial stage transition from: ${stage}`);
                    nextStage = 'intro';
            }
            nextStep = 0;
        }
        setStage(nextStage);
        setStep(nextStep);
    }, [stage, step, navigate]);


    // --- Tutorial Response Handler ---
    const handleTutorialResponse = useCallback((type) => {
        if (!currentStepConfig?.promptButton) return;

        let correct = false;
        if (currentStepConfig.promptButton === 'both') {
            correct = true;
        } else {
            correct = currentStepConfig.promptButton === type;
        }

        audioManager.playFeedbackSound(correct ? 'correct' : 'incorrect'); // Play sound

        setFeedbackState(prev => ({ ...prev, [type]: correct ? 'correct' : 'incorrect' }));

        setTimeout(() => {
             setFeedbackState(prev => ({ ...prev, [type]: null }));
        }, 300);
    }, [currentStepConfig]);


    // --- Determine current stimuli ---
    const stim = currentSequence[currentTrialIndex] || { position: null, letter: null };

    // --- Render Component ---
    return (
        <div className="h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl relative mb-4 flex flex-col" style={{ minHeight: '75vh' }}>

                 <button
                    onClick={() => { audioManager.stopCurrentSound(); navigate('/dashboard'); }}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-2xl z-20 leading-none p-1"
                    title="Exit Tutorial" aria-label="Exit Tutorial"
                > × </button>

                <GameHeader
                    nBack={currentNBack} difficulty={0} currentTrial={currentTrialIndex} totalTrials={totalTrials} gameState="tutorial"
                    onPauseResume={() => {}} isNoviceLevel={true} showNoviceHelp={true} onToggleNoviceHelp={() => {}}
                />

                {/* Game Area */}
                <div className="flex mt-6 flex-grow">
                    <div className="flex-1 flex items-center justify-center">
                        <PositionGrid
                            sequence={currentSequence} currentTrial={currentTrialIndex} nBack={currentNBack} showNoviceHelp={true}
                            isTutorialMode={true} activePosition={stim.position}
                            feedbackState={feedbackState.visual}
                        />
                    </div>
                    <div className="w-24 flex items-center justify-center">
                        <AudioLetterDisplay
                             sequence={currentSequence} currentTrial={currentTrialIndex} nBack={currentNBack} showNoviceHelp={true}
                             isTutorialMode={true} activeLetter={stim.letter}
                             feedbackState={feedbackState.audio}
                        />
                    </div>
                </div>

                 {/* Response Buttons */}
                 <div className="grid grid-cols-2 gap-4 mt-8 h-14">
                    <MatchButton
                        label="Position Match" keyHint="←"
                        onClick={() => handleTutorialResponse('visual')}
                        feedbackState={feedbackState.visual}
                        disabled={!(promptButton === 'visual' || promptButton === 'both')}
                    />
                     <MatchButton
                        label="Audio Match" keyHint="→"
                        onClick={() => handleTutorialResponse('audio')}
                        feedbackState={feedbackState.audio}
                        disabled={!(promptButton === 'audio' || promptButton === 'both')}
                    />
                </div>

                {/* Tutorial Instructions & Controls */}
                 <div className="mt-6 pt-4 border-t border-gray-200 text-center">
                    <p className="text-gray-700 mb-4 min-h-[3em] px-4">
                        {currentStepConfig?.text || ' '}
                    </p>
                    <button
                        onClick={handleNext}
                        className="px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-150 shadow-sm"
                    >
                        {stage === 'outro' ? 'Finish Tutorial' : 'Next'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default TutorialScreen;